import React, { useMemo, useState } from 'react';
import TrendChart from './TrendChart';
import PeakHoursHeatmap from './PeakHoursHeatmap';
import AgentComparisonChart from './AgentComparisonChart';
import DateRangePicker from './DateRangePicker';
import {
  filterCallsByDate,
  localDateStr,
  buildAgentStats,
  daysAgo,
  buildDailyPoints,
  buildHeatmapGrid,
} from './callUtils';

const PRESETS = [
  { key: '7', label: '7 Days', days: 7 },
  { key: '14', label: '14 Days', days: 14 },
  { key: '30', label: '30 Days', days: 30 },
];

function TrendAnalytics({ calls }) {
  const today = localDateStr(new Date());
  const [fromDate, setFromDate] = useState(daysAgo(14));
  const [toDate, setToDate] = useState(today);

  const activePreset = PRESETS.find((preset) => fromDate === daysAgo(preset.days) && toDate === today)?.key || 'custom';

  const applyPreset = (preset) => {
    setFromDate(daysAgo(preset.days));
    setToDate(today);
  };

  const points = useMemo(() => {
    if (!fromDate || !toDate || fromDate > toDate) return [];
    return buildDailyPoints(calls, fromDate, toDate);
  }, [calls, fromDate, toDate]);

  const rangeCalls = useMemo(() => {
    if (!fromDate || !toDate || fromDate > toDate) return [];
    return filterCallsByDate(calls, fromDate, toDate);
  }, [calls, fromDate, toDate]);

  const heatmapGrid = useMemo(() => buildHeatmapGrid(rangeCalls), [rangeCalls]);
  const agentStats = useMemo(() => buildAgentStats(rangeCalls), [rangeCalls]);

  const totals = useMemo(
    () =>
      points.reduce(
        (acc, p) => ({
          answered: acc.answered + p.answered,
          missed: acc.missed + p.missed,
          attempted: acc.attempted + p.attempted,
        }),
        { answered: 0, missed: 0, attempted: 0 }
      ),
    [points]
  );
  const totalCalls = totals.answered + totals.missed + totals.attempted;
  const answerRate = totalCalls > 0 ? Math.round((totals.answered / totalCalls) * 100) : null;

  return (
    <div className="agent-page">
      <div className="agent-page-header">
        <h2 className="agent-page-title">Call Trends</h2>
        <div className="agent-date-filter">
          <div className="customer-toggle" role="group" aria-label="Date range preset">
            {PRESETS.map((preset) => (
              <button
                key={preset.key}
                type="button"
                className={activePreset === preset.key ? 'active' : ''}
                onClick={() => applyPreset(preset)}
              >
                {preset.label}
              </button>
            ))}
          </div>
          <DateRangePicker
            fromDate={fromDate}
            toDate={toDate}
            maxDate={today}
            onApply={(from, to) => {
              setFromDate(from);
              setToDate(to);
            }}
          />
        </div>
      </div>

      <div className="stat-cards">
        <div className="stat-card stat-card-static">
          <div className="stat-value">{totalCalls}</div>
          <div className="stat-label">Total Calls</div>
        </div>
        <div className="stat-card stat-card-static answered">
          <div className="stat-value">{totals.answered}</div>
          <div className="stat-label">Answered</div>
        </div>
        <div className="stat-card stat-card-static missed">
          <div className="stat-value">{totals.missed}</div>
          <div className="stat-label">Missed</div>
        </div>
        <div className="stat-card stat-card-static attempted">
          <div className="stat-value">{totals.attempted}</div>
          <div className="stat-label">Attempted</div>
        </div>
        <div className="stat-card stat-card-static">
          <div className="stat-value">{answerRate === null ? '-' : `${answerRate}%`}</div>
          <div className="stat-label">Answer Rate</div>
        </div>
      </div>

      {fromDate > toDate ? (
        <p className="agent-page-empty">"From" date must be before "To" date.</p>
      ) : (
        <>
          <TrendChart points={points} />
          <AgentComparisonChart agents={agentStats} />
          <PeakHoursHeatmap grid={heatmapGrid} />
        </>
      )}
    </div>
  );
}

export default TrendAnalytics;
